// src/pages/Calendar.jsx
import { useEffect, useState } from "react";
import {
    Container,
    Typography,
    Box,
    ToggleButton,
    ToggleButtonGroup,
    IconButton,
    Tooltip,
} from "@mui/material";

import SortIcon from "@mui/icons-material/Sort";
import EventIcon from "@mui/icons-material/Event";

import EventCard from "../components/EventCard";

// 🌈 Gradients animés par sport
const SPORT_GRADIENTS = {
    "course à pied": { from: "#ff6f00", to: "#ff8f00", glow: "rgba(255,143,0,0.45)" },
    "ski alpin": { from: "#1976d2", to: "#42a5f5", glow: "rgba(66,165,245,0.45)" },
    telemark: { from: "#6a1b9a", to: "#ab47bc", glow: "rgba(171,71,188,0.45)" },
    pumptrack: { from: "#d32f2f", to: "#ef5350", glow: "rgba(239,83,80,0.45)" },
    vtt: { from: "#2e7d32", to: "#66bb6a", glow: "rgba(102,187,106,0.45)" },
};

// 🔠 Label mois FR
const getMonthLabel = (date) =>
    date.toLocaleString("fr-CH", { month: "long", year: "numeric" });

// 🧠 PARSEUR ROBUSTE — l'API renvoie du "YYYY-MM-DD" (branche ISO), mais on
// garde les autres formats en filet de sécurité si la source change un jour.
const parseDate = (d) => {
    if (!d) return null;
    const clean = d.replace(/\r/g, "").trim();

    // 1) Format suisse : 12.03.2025
    const swiss = clean.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/);
    if (swiss) {
        let [_, day, month, year] = swiss;
        if (year.length === 2) year = "20" + year;
        return new Date(`${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T00:00:00`);
    }

    // 2) Format ISO : 2025-03-12
    const iso = clean.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (iso) {
        const [_, year, month, day] = iso;
        return new Date(`${year}-${month}-${day}T00:00:00`);
    }

    // 3) Format slash : 12/03/2025
    const slash = clean.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
    if (slash) {
        let [_, day, month, year] = slash;
        if (year.length === 2) year = "20" + year;
        return new Date(`${year}-${month}-${day}T00:00:00`);
    }

    // 4) Tentative finale
    const fallback = new Date(clean);
    return isNaN(fallback) ? null : fallback;
};

// 🧠 Ajoute X jours à une date (utilitaire)
const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};

export default function Calendar() {
    const [events, setEvents] = useState([]);
    const [sortOrder, setSortOrder] = useState("asc");
    const [filter, setFilter] = useState("future");

    useEffect(() => {
        fetch("/api/calendar-events")
            .then((res) => res.json())
            .then((data) => setEvents(Array.isArray(data) ? data : []))
            .catch(() => setEvents([]));
    }, []);

    const now = new Date();

    // 🔍 Filtrage robuste (un évènement reste "à venir" jusqu'à la fin de son jour)
    const filteredEvents = events.filter((ev) => {
        const date = parseDate(ev.date);
        if (!date) return false;
        const endOfEventDay = addDays(date, 1); // minuit du lendemain
        if (filter === "future") return endOfEventDay > now;
        if (filter === "past") return endOfEventDay <= now;
        return true;
    });

    // 🔥 Tri
    const sortedEvents = [...filteredEvents].sort((a, b) => {
        const da = parseDate(a.date);
        const db = parseDate(b.date);
        if (filter === "past") return db - da;
        return sortOrder === "asc" ? da - db : db - da;
    });

    // 📦 Regroupement par mois
    const eventsByMonth = sortedEvents.reduce((acc, ev) => {
        const d = parseDate(ev.date);
        const key = getMonthLabel(d);
        if (!acc[key]) acc[key] = [];
        acc[key].push(ev);
        return acc;
    }, {});

    return (
        <Container maxWidth="md" sx={{ mt: 6, mb: 6 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <EventIcon sx={{ color: "primary.main", mr: 1 }} />
                <Typography variant="h3">Calendrier des évènements</Typography>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 3,
                    alignItems: "center",
                }}
            >
                <ToggleButtonGroup
                    value={filter}
                    exclusive
                    onChange={(e, val) => val && setFilter(val)}
                >
                    <ToggleButton value="future">À venir</ToggleButton>
                    <ToggleButton value="past">Passés</ToggleButton>
                    <ToggleButton value="all">Tous</ToggleButton>
                </ToggleButtonGroup>

                <Tooltip title="Changer l'ordre">
                    <IconButton
                        onClick={() =>
                            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
                        }
                    >
                        <SortIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {Object.entries(eventsByMonth).map(([month, items]) => (
                <Box key={month} sx={{ width: "100%", mb: 4 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            mb: 2,
                            mt: 4,
                            textTransform: "capitalize",
                            borderBottom: "2px solid #ddd",
                            pb: 1,
                        }}
                    >
                        {month}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            // 🔄 Téléphone en paysage : impossible de le distinguer
                            // d'une petite fenêtre desktop par la seule largeur (les
                            // deux peuvent faire ~700-900px). On cible donc plutôt
                            // une hauteur d'écran limitée en mode paysage — signe
                            // fiable d'un téléphone, pas d'un ordinateur — pour
                            // forcer 1 seule carte par ligne dans ce cas précis.
                            "@media (orientation: landscape) and (max-height: 500px)": {
                                "& > *": { flexBasis: "100% !important" },
                            },
                        }}
                    >
                        {items.map((ev, index) => {
                            const grad =
                                SPORT_GRADIENTS[ev.sport?.toLowerCase()] || {
                                    from: "#777",
                                    to: "#aaa",
                                    glow: "rgba(120,120,120,0.4)",
                                };

                            return (
                                <Box
                                    key={ev.id ?? ev.nom + index}
                                    sx={{ flex: "0 1 340px", minWidth: 0, margin: "30px" }}
                                >
                                    <EventCard ev={ev} index={index} gradient={grad} />
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            ))}

            <Typography variant="caption" sx={{ display: "block", mt: 4, color: "text.secondary" }}>
                <a href="/calendrier.html" style={{ color: "inherit" }}>
                    Version texte du calendrier
                </a>
            </Typography>
        </Container>
    );
}